import React, { useEffect, useMemo, useState } from "react";
import { api, fmtMoney, fmtDate, formatApiError, todayLocal } from "@/lib/api";
import { attachmentUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import ListSearch, { filterBy } from "@/components/ListSearch";
import { CreditCard, ArrowDownRight, ArrowUpRight, Pencil, Trash2, Paperclip, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import AttachReceiptDialog from "@/components/AttachReceiptDialog";
import EditPaymentDialog, { DeletePaymentDialog } from "@/components/EditPaymentDialog";

const METHOD_LABEL = {
    manual: "Cash / manual",
    bank: "Bank transfer",
    card: "Card",
    cheque: "Cheque",
    other: "Other",
};

export default function Payments() {
    const { currentOrg } = useAuth();
    const [tab, setTab] = useState("all");
    const [rows, setRows] = useState([]);
    const [totals, setTotals] = useState({ received: 0, made: 0, net: 0 });
    const [bankAccounts, setBankAccounts] = useState([]);
    const [editTarget, setEditTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [attachTarget, setAttachTarget] = useState(null);
    const [q, setQ] = useState("");

    const load = async () => {
        const [{ data }, { data: ba }] = await Promise.all([
            api.get(`/payments?direction=${tab}`),
            api.get("/bank-accounts").catch(() => ({ data: { items: [] } })),
        ]);
        setRows(data.items || []);
        setTotals(data.totals || { received: 0, made: 0, net: 0 });
        setBankAccounts(ba.items || []);
    };

    useEffect(() => { if (currentOrg) load(); }, [currentOrg, tab]);

    const currency = currentOrg?.currency || "KYD";
    const summary = useMemo(() => [
        { label: "Received", value: totals.received, tone: "in", icon: ArrowDownRight },
        { label: "Made", value: totals.made, tone: "out", icon: ArrowUpRight },
        { label: "Net", value: totals.net, tone: totals.net >= 0 ? "in" : "out", icon: CreditCard },
    ], [totals]);

    const accountName = (id) => bankAccounts.find((a) => a.id === id)?.name || "—";

    return (
        <div>
            <PageHeader
                title="Payments"
                description="Every money movement — payments received from customers and payments made to vendors — in one chronological ledger."
            />

            <div className="grid sm:grid-cols-3 gap-4 mb-6">
                {summary.map((s) => (
                    <div key={s.label} className="rounded-3xl bg-white border border-sand-200 p-5 shadow-card">
                        <div className="flex items-center justify-between">
                            <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{s.label}</div>
                            <div className={`h-8 w-8 rounded-xl grid place-items-center ${s.tone === "in" ? "bg-secondary/10 text-secondary-dark" : "bg-coral/10 text-coral-dark"}`}>
                                <s.icon className="h-4 w-4" />
                            </div>
                        </div>
                        <div className="font-mono text-2xl text-ocean-700 mt-2" data-testid={`payments-total-${s.label.toLowerCase()}`}>
                            {fmtMoney(s.value, currency)}
                        </div>
                    </div>
                ))}
            </div>

            <Tabs value={tab} onValueChange={setTab} className="mb-4">
                <TabsList>
                    <TabsTrigger value="all" data-testid="payments-tab-all">All</TabsTrigger>
                    <TabsTrigger value="received" data-testid="payments-tab-received">Received</TabsTrigger>
                    <TabsTrigger value="made" data-testid="payments-tab-made">Made</TabsTrigger>
                </TabsList>
            </Tabs>

            <div className="mb-4 flex items-center gap-3">
                <ListSearch value={q} onChange={setQ} placeholder="Search payments by party, reference or doc #…" testId="payments-search" />
                {q && <span className="text-xs text-muted-foreground">{filterBy(rows, q, ["party_name", "reference", "invoice_number", "method"]).length} of {rows.length}</span>}
            </div>

            {rows.length === 0 ? (
                <EmptyState
                    icon={CreditCard}
                    title="No payments yet"
                    description="Payments recorded against invoices or bills will show up here."
                />
            ) : (
                <div className="rounded-3xl bg-white border border-sand-200 shadow-card overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-sand-100 text-xs uppercase tracking-wider text-muted-foreground">
                                <tr>
                                    <th className="text-left px-5 py-3">Date</th>
                                    <th className="text-left px-5 py-3">Direction</th>
                                    <th className="text-left px-5 py-3">Doc #</th>
                                    <th className="text-left px-5 py-3">Party</th>
                                    <th className="text-left px-5 py-3">Method</th>
                                    <th className="text-left px-5 py-3">Account</th>
                                    <th className="text-left px-5 py-3">Reference</th>
                                    <th className="text-right px-5 py-3">Amount</th>
                                    <th className="px-5 py-3"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-sand-200">
                                {filterBy(rows, q, ["party_name", "reference", "invoice_number", "method"]).map((p) => (
                                    <tr key={`${p.direction}-${p.id}`} className="hover:bg-sand-50/50" data-testid={`payment-row-${p.id}`}>
                                        <td className="px-5 py-3 text-muted-foreground whitespace-nowrap">{fmtDate(p.paid_on)}</td>
                                        <td className="px-5 py-3">
                                            {p.direction === "received" ? (
                                                <Badge className="border-0 bg-secondary/10 text-secondary-dark">
                                                    <ArrowDownRight className="h-3 w-3 mr-1" /> Received
                                                </Badge>
                                            ) : (
                                                <Badge className="border-0 bg-coral/10 text-coral-dark">
                                                    <ArrowUpRight className="h-3 w-3 mr-1" /> Made
                                                </Badge>
                                            )}
                                        </td>
                                        <td className="px-5 py-3 font-mono text-xs">{p.invoice_number || "—"}</td>
                                        <td className="px-5 py-3 font-medium text-ocean-700">{p.party_name || "—"}</td>
                                        <td className="px-5 py-3 text-muted-foreground">{METHOD_LABEL[p.method] || p.method}</td>
                                        <td className="px-5 py-3 text-muted-foreground">{accountName(p.account_id)}</td>
                                        <td className="px-5 py-3 text-muted-foreground text-xs">{p.reference || "—"}</td>
                                        <td className={`px-5 py-3 font-mono text-right font-medium ${p.direction === "received" ? "text-secondary-dark" : "text-coral-dark"}`}>
                                            {p.direction === "received" ? "+" : "−"}{fmtMoney(p.amount, p.currency || currency)}
                                        </td>
                                        <td className="px-5 py-3 text-right whitespace-nowrap">
                                            {p.attachment_id ? (
                                                <a
                                                    href={attachmentUrl(p.attachment_id)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center justify-center h-8 w-8 rounded-md text-secondary-dark hover:bg-secondary/10 mr-1"
                                                    title="View receipt"
                                                    data-testid={`payment-receipt-${p.id}`}
                                                >
                                                    <Paperclip className="h-4 w-4" />
                                                </a>
                                            ) : null}
                                            {p.direction === "made" && (
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => setAttachTarget(p)}
                                                    title={p.attachment_id ? "Replace or remove receipt" : "Attach a receipt"}
                                                    data-testid={`attach-receipt-${p.id}`}
                                                >
                                                    {p.attachment_id ? (
                                                        <Pencil className="h-4 w-4 text-muted-foreground" />
                                                    ) : (
                                                        <Paperclip className="h-4 w-4 text-muted-foreground" />
                                                    )}
                                                </Button>
                                            )}
                                            {p.source === "expense" ? (
                                                <span className="text-xs text-muted-foreground pr-2" title="Edit from Expenses page">Expense</span>
                                            ) : (
                                                <>
                                                    <Button variant="ghost" size="icon" onClick={() => setEditTarget(p)} data-testid={`edit-payment-${p.id}`} title="Edit payment"><Pencil className="h-4 w-4 text-muted-foreground" /></Button>
                                                    <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(p)} data-testid={`delete-payment-${p.id}`} title="Delete payment"><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <EditPaymentDialog
                payment={editTarget}
                bankAccounts={bankAccounts}
                onClose={() => setEditTarget(null)}
                onSaved={() => { setEditTarget(null); load(); }}
            />
            <DeletePaymentDialog
                payment={deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onDeleted={() => { setDeleteTarget(null); load(); }}
            />
            <AttachReceiptDialog
                payment={attachTarget}
                onClose={() => setAttachTarget(null)}
                onSaved={() => { setAttachTarget(null); load(); }}
            />
        </div>
    );
}
